import {
    useForm,
    UseFormReturn,
    DefaultValues,
    useWatch,
} from 'react-hook-form'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export function useFilterParams<T extends object>(
    key: string,
    defaultValues: T
): {
    methods: UseFormReturn<T, any, undefined>
    values: Partial<T>
    encodedFilters?: string
} {
    const [searchParams, setSearchParams] = useSearchParams()

    const parseDefaultValues = (): DefaultValues<T> => {
        try {
            const encoded = searchParams.get(`${key}filters`)
            if (!encoded) return defaultValues as DefaultValues<T>
            return JSON.parse(atob(encoded)) as DefaultValues<T>
        } catch {
            return defaultValues as DefaultValues<T>
        }
    }

    const methods = useForm<T, any, undefined>({
        defaultValues: parseDefaultValues(),
    })

    const watchedValues = useWatch({ control: methods.control })

    const values = useMemo(() => {
        return Object.entries(watchedValues)
            .filter(([_, value]) =>
                Array.isArray(value) ? value.length > 0 : !!value
            )
            .reduce((acc, [key, value]) => {
                acc[key as keyof T] = value
                return acc
            }, {} as Partial<T>)
    }, [watchedValues])

    useEffect(() => {
        if (Object.keys(values).length === 0) {
            setSearchParams(
                (prev) => {
                    prev.delete(`${key}filters`)
                    return prev
                },
                { replace: true }
            )
        } else {
            setSearchParams((prev) => {
                prev.set(`${key}filters`, btoa(JSON.stringify(values)))
                return prev
            })
        }
    }, [values])

    return {
        methods,
        values,
        encodedFilters: btoa(JSON.stringify(values)),
    }
}
